import { t } from "i18next";

import prettyDurations, { DURATION_UNITS } from "./durations";
import { DEFAULT_FRAME_RATE } from "../constants/media";

// freemium_end accepts exactly HH:MM:SS, HH:MM:SS:FF (frames, 2 digits, ":"
// separator) or HH:MM:SS.sss (milliseconds, 3 digits, "." separator). MM/SS are
// bounded to 00-59. An empty value is allowed (no freemium marker). Capture
// groups feed freemiumEndToMs so validation and parsing share one pattern.
export const FREEMIUM_END_PATTERN =
  /^(\d{2}):([0-5]\d):([0-5]\d)(?::(\d{2})|\.(\d{3}))?$/;

// Matches the reason field returned by the API so error keys resolve to the
// same "error:freemium_end.<reason>" translations.
export type FreemiumEndErrorReason = "invalid_format" | "exceeds_duration";

export function isValidFreemiumEnd(value: string): boolean {
  return value === "" || FREEMIUM_END_PATTERN.test(value);
}

export function freemiumEndToMs(value: string): number | null {
  const match = FREEMIUM_END_PATTERN.exec(value);
  if (!match) return null;

  const [, hours, minutes, seconds, frames, milliseconds] = match;
  const baseMs =
    (Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds)) * 1000;

  if (frames !== undefined) {
    return baseMs + Math.floor((Number(frames) / DEFAULT_FRAME_RATE) * 1000);
  }
  if (milliseconds !== undefined) {
    return baseMs + Number(milliseconds);
  }
  return baseMs;
}

export function getFreemiumEndError(
  value: string,
  durationMs: number | null,
): FreemiumEndErrorReason | null {
  if (!isValidFreemiumEnd(value)) return "invalid_format";

  const valueMs = freemiumEndToMs(value);
  if (durationMs !== null && valueMs !== null && valueMs > durationMs) {
    return "exceeds_duration";
  }

  return null;
}

// Translated error message for a freemium_end value, or null when it is valid.
// The exceeds-duration case embeds the media duration so the user sees the
// allowed ceiling.
export function getFreemiumEndErrorMessage(
  value: string,
  durationMs: number | null,
): string | null {
  const reason = getFreemiumEndError(value, durationMs);
  if (reason === null) return null;

  if (reason === "exceeds_duration" && durationMs !== null) {
    const mediaDuration = prettyDurations(durationMs, {
      valueUnit: DURATION_UNITS.MILLISECONDS,
    }).timecodeDuration;
    return t("editor-content:durationExceedsMediaDuration", { mediaDuration });
  }

  return t(`error:freemium_end.${reason}`);
}
